import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';

import { makeGetProject } from '../selectors/projects';
import { makeGetSortedActiveProjectMilestones, makeGetSortedArchivedProjectMilestones } from '../selectors/milestones';
import { makeGetSortedActiveProjectTaskGroups } from '../selectors/task-groups';
import { makeGetFetchedState, makeGetLoadingState } from '../selectors/global';

import { fetchTaskGroups, createTaskGroup } from '../actions/task-groups';
import { fetchTasks, updateTask } from '../actions/tasks';
import { fetchMilestones, updateMilestone, untieMilestoneTasks } from '../actions/milestones';
import { showModal } from '../actions/modal';

import { trackEvent } from '../services/events-tracker-service';
import * as userStorageService from '../services/user-storage-service';

import AppInnerPortal from '../portals/app-inner-portal';

import { TasksDNDProvider } from 'context/TasksDNDContext';

import MilestoneOverview from './milestone-overview';
import MilestoneOverviewHeadItem from './milestone-overview-head-item';
import TaskSingle from './task-single';
import MilestoneSingle from './milestone-single';
import CanUser from './can-user';
import TaskDNDDraggable from 'components/TaskDNDDraggable';
import CreateTaskButton from './create-task-button';
import CreateTaskList from 'components/CreateTaskList';
import MilestonesToggle from 'components/MilestonesToggle';
import TasksWithoutMilestone from 'components/TasksWithoutMilestone';
import TaskGroupTasksFetcher from 'components/TaskGroupTasksFetcher';
import TaskGroupsFetcher from 'components/TaskGroupsFetcher';

import IconPlus from 'icons/icon-plus';

const ProjectOverview = ({ id, currentTaskId, currentSubtaskId, milestones, archivedMilestones, taskGroups, viewType, milestonesLoading, milestonesFetched, taskGroupsLoading, taskGroupsFetched, onTaskClose, t }) => {
    const [currentTaskIdState, setCurrentTaskIdState] = useState(null);
    const [currentMilestoneId, setCurrentMilestoneId] = useState(null);
    const [showArchived, setShowArchived] = useState(false);
    const [collapsed, setCollapsed] = useState((id && userStorageService.get(`projectOverviewCollapsedMilestones-${id}`)) || []);

    const handleTaskClick = useCallback(taskId => {
        setCurrentTaskIdState(taskId);
    }, [setCurrentTaskIdState]);

    const handleTaskClose = useCallback(() => {
        setCurrentTaskIdState(null);
        onTaskClose && onTaskClose();
    }, [setCurrentTaskIdState]);

    const handleMilestoneClick = useCallback(milestoneId => {
        setCurrentMilestoneId(milestoneId);
    }, [setCurrentMilestoneId]);

    const handleMilestoneClose = useCallback(() => {
        setCurrentMilestoneId(null);
    }, [setCurrentMilestoneId]);

    const handleMilestoneEdit = useCallback((milestoneId, milestoneProps) => {
        updateMilestone(milestoneId, milestoneProps);

        if (milestoneProps.removedTasks && milestoneProps.removedTasks.length) {
            untieMilestoneTasks(milestoneId, milestoneProps.removedTasks);
        }
    }, []);

    const handleAddMilestoneClick = useCallback(() => {
        showModal('MILESTONE_CREATE', {
            projectId: id,
            milestones
        });
    }, [id, milestones]);

    const handleArchivedToggle = useCallback(({ showArchived }) => {
        setShowArchived(showArchived);
    }, [setShowArchived]);

    const handleGroupCreate = useCallback(name => {
        trackEvent('TASKLIST.CREATE');

        createTaskGroup({
            project: id,
            name,
            order: Number.POSITIVE_INFINITY
        });
    }, [id, taskGroups.length])

    const handleCollapse = useCallback(id => {
        const nextState = [...collapsed, id];

        setCollapsed(nextState);

        userStorageService.set(`projectOverviewCollapsedMilestones-${id}`, nextState);
    }, [id, collapsed, setCollapsed]);

    const handleExpand = useCallback(id => {
        const nextState = collapsed.filter(milestoneId => milestoneId !== id);

        setCollapsed(nextState);

        userStorageService.set(`projectOverviewCollapsedMilestones-${id}`, nextState);
    }, [id, collapsed, setCollapsed]);

    const handleKeyDown = useCallback(event => {
        if (event.which === 27 && (event.target.nodeName !== 'INPUT' && event.target.nodeName !== 'TEXTAREA')) {
            setCurrentTaskIdState(null);
            setCurrentMilestoneId(null);
        }
    }, [setCurrentTaskIdState, setCurrentMilestoneId]);

    const screenMilestones = useMemo(() => {
        if (showArchived) {
            return archivedMilestones;
        }

        return milestones;
    }, [showArchived, archivedMilestones, milestones]);

    useEffect(() => {
        if (!milestonesLoading && !milestonesFetched) {
            fetchMilestones(id);
        }
    }, [id, milestonesLoading, milestonesFetched]);

    useEffect(() => {
        if (id) {
            setCollapsed((userStorageService.get(`projectOverviewCollapsedMilestones-${id}`)) || []);
        }
    }, [id]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        }
    }, []);

    return (
        <TasksDNDProvider withMilestone={true}>
            <TaskGroupsFetcher projectId={id} />

            <div className="project-overview">
                <div className="project-overview-head">
                    <div className="project-overview-head-main">
                        <div className="project-overview-head-main-inner">
                            <MilestonesToggle
                                count={screenMilestones.length}
                                onToggle={handleArchivedToggle}
                            />
                        </div>

                        {
                            !showArchived ? (
                                <button className="btn btn-xxxsmall btn-action btn-action-white" onClick={handleAddMilestoneClick}>
                                    <IconPlus />
                                </button>
                            ) : null
                        }
                    </div>

                    {
                        taskGroups.map(taskGroup => (
                            <React.Fragment key={taskGroup.id}>
                                <TaskGroupTasksFetcher id={taskGroup.id} includeSubtasks={true} />

                                <MilestoneOverviewHeadItem id={taskGroup.id} />
                            </React.Fragment>
                        ))
                    }

                    <TaskGroupTasksFetcher isStatic={true} type="completed" projectId={id} />

                    <MilestoneOverviewHeadItem key="completed" id="completed" name={ t('common:Completed') } completed={true} hideAssignees={true} hideUtils={true} />

                    <CreateTaskList onSubmit={handleGroupCreate} />
                </div>

                <div className="project-overview-body">
                    <TasksWithoutMilestone
                        projectId={id}
                        taskGroups={taskGroups}
                        collapsed={collapsed.includes('completed')}
                        onTaskClick={handleTaskClick}
                        onCollapse={handleCollapse}
                        onExpand={handleExpand}
                    />

                    {
                        screenMilestones.map(milestone => <MilestoneOverview
                            key={milestone.id}
                            id={milestone.id}
                            projectId={id}
                            collapsed={collapsed.includes(milestone.id)}
                            onTaskClick={handleTaskClick}
                            onMilestoneClick={handleMilestoneClick}
                            onEdit={handleMilestoneEdit}
                            onCollapse={handleCollapse}
                            onExpand={handleExpand}
                        />)
                    }

                    {
                        !showArchived && !screenMilestones.length ? (
                            <div className="milestone-overview">
                                <div className="overview-main">
                                    <div className="overview-main-empty">
                                        <CanUser type="createMilestone">
                                            <button className="btn btn-medium btn-success btn-block" onClick={handleAddMilestoneClick}>
                                                + { t('Create a milestone') }
                                            </button>
                                        </CanUser>
                                    </div>
                                </div>

                                <div className="overview-task-groups">
                                    {
                                        taskGroups.map(taskGroup => (
                                            <div key={taskGroup.id} className="overview-group">
                                                <div className="overview-group-list task-group">
                                                    <TaskDNDDraggable
                                                        key="empty"
                                                        index={0}
                                                        viewType={viewType}
                                                        name={ t('help.tasksTie') }
                                                        taskGroupId={taskGroup.id}
                                                        placeholder={true}
                                                    />

                                                    <div className="group-foot tour-target-task-create">
                                                        <CreateTaskButton disabled={true} />
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                        ) : null
                    }
                </div>
            </div>

            <AppInnerPortal>
                <TaskSingle
                    id={currentTaskId || currentTaskIdState}
                    subtaskId={currentSubtaskId}
                    noRedirect={true}
                    shouldChangeUrl={false}
                    onClose={handleTaskClose}
                />

                <MilestoneSingle
                    id={currentMilestoneId}
                    onClose={handleMilestoneClose}
                />
            </AppInnerPortal>
        </TasksDNDProvider>
    )

}

const makeMapStateToProps = () => {
    const getProject = makeGetProject();
    const getSortedActiveProjectMilestones = makeGetSortedActiveProjectMilestones();
    const getSortedActiveProjectTaskGroups = makeGetSortedActiveProjectTaskGroups();
    const getSortedArchivedProjectMilestones = makeGetSortedArchivedProjectMilestones();
    const getMilestonesFetched = makeGetFetchedState();
    const getMilestonesLoading = makeGetLoadingState();

    return ({ projects, taskGroups, milestones, tasks }, ownProps) => {
        const project = getProject(projects.items, ownProps.id);

        return {
            project,
            milestones: getSortedActiveProjectMilestones(milestones.items, ownProps.id),
            archivedMilestones: getSortedArchivedProjectMilestones(milestones.items, ownProps.id),
            taskGroups: getSortedActiveProjectTaskGroups(taskGroups.items, ownProps.id),
            milestonesFetched: getMilestonesFetched(milestones.fetchInfo, 'project', ownProps.id),
            milestonesLoading: getMilestonesLoading(milestones.fetchInfo, 'project', ownProps.id)
        }
    }
}

export default connect(makeMapStateToProps)(withTranslation(['milestone', 'common'])(ProjectOverview));
